// src/Views/BookmarksView.js
import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { BookmarksContext } from '../contexts/BookmarksContext';
import { AuthContext } from '../contexts/AuthContext';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
  SPACING_HERO,
} from '../constants/layout';
import {
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_FOOTNOTE,
} from '../constants/typography';
import ContentUnavailableView from './ContentUnavailableView';
import { buildProductPath } from '../utils/productSlug';
import { AUTH_ACTION_LABEL } from '../constants/settings';

function BookmarksView() {
  const { bookmarks, removeBookmark } = useContext(BookmarksContext);
  const { user } = useContext(AuthContext);
  const [allProducts, setAllProducts] = useState([]);
  const [allImages, setAllImages] = useState([]);

  // Fetch all products for displaying bookmarks
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'products'), (snapshot) => {
      const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProducts(products);
    });

    return () => unsubscribe();
  }, []);

  // Fetch all images for product thumbnails
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'imageItems'), (snapshot) => {
      const images = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllImages(images);
    });

    return () => unsubscribe();
  }, []);

  // Helper to get product by ID
  const getProductById = (productID) => {
    return allProducts.find(p => p.id === productID);
  };

  // Helper to get product image
  const getProductImage = (product) => {
    if (!product) return null;

    // Try to find image by imageItemID
    if (product.productImage?.imageItemID) {
      const image = allImages.find(img => img.id === product.productImage.imageItemID);
      if (image) return image.thumbnailURL || image.imageURL;
    }

    // Try to find image by productID
    const image = allImages.find(img => img.productID === product.id);
    return image?.thumbnailURL || image?.imageURL || null;
  };

  if (!user) {
    return (
      <ContentUnavailableView
        icon="bookmark_border"
        title="Save Your Bookmarks"
        description={`${AUTH_ACTION_LABEL} to save and sync your bookmarked products across devices.`}
        actions={[{ label: AUTH_ACTION_LABEL, to: '/login' }]}
      />
    );
  }

  if (bookmarks.length === 0) {
    return (
      <ContentUnavailableView
        icon="bookmark_border"
        title="No Bookmarks"
        description="Products you bookmark will appear here."
      />
    );
  }

  return (
    <div className="bookmarks-container">
      <div className="bookmarks-header">
        <h1 className="section-title">Bookmarks</h1>
      </div>

      <div className="bookmarks-grid">
        {bookmarks.map(bookmark => {
          const product = getProductById(bookmark.productID);
          if (!product) return null;
          if (product.deactivatedDate != null) return null;

          const imageURL = getProductImage(product);

          return (
            <div key={bookmark.id} className="bookmark-item">
              <Link to={buildProductPath(product)} className="bookmark-link">
                {imageURL ? (
                  <div className="bookmark-image-container">
                    <img
                      src={imageURL}
                      alt={product.productName}
                      className="bookmark-image"
                    />
                  </div>
                ) : (
                  <div className="bookmark-placeholder">
                    <span className="material-icons">photo</span>
                  </div>
                )}
                <div className="bookmark-info">
                  <p className="bookmark-name">{product.productName}</p>
                </div>
              </Link>
              <button
                onClick={() => removeBookmark(product.id)}
                className="remove-bookmark-button"
                aria-label="Remove from bookmarks"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .bookmarks-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_BUTTON} ${SPACING_SECTION};
        }

        .bookmarks-header {
          margin-bottom: ${SPACING_CARD};
        }

        .section-title {
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-base);
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
        }

        .bookmarks-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: ${SPACING_BUTTON};
        }

        .bookmark-item {
          position: relative;
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }


        .bookmark-link {
          display: block;
          text-decoration: none;
          color: inherit;
        }

        .bookmark-image-container {
          width: 100%;
          aspect-ratio: 1;
          overflow: hidden;
          background-color: var(--color-surface-muted);
        }

        .bookmark-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .bookmark-item:hover .bookmark-image {
          transform: scale(1.05);
        }

        .bookmark-placeholder {
          width: 100%;
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-surface-muted);
        }

        .bookmark-placeholder .material-icons {
          font-size: 48px;
          color: var(--color-text-secondary);
          opacity: 0.5;
        }

        .bookmark-info {
          padding: ${SPACING_COMPONENT};
        }

        .bookmark-name {
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .remove-bookmark-button {
          position: absolute;
          top: ${SPACING_ELEMENT};
          right: ${SPACING_ELEMENT};
          width: 32px;
          height: 32px;
          border: none;
          background-color: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all 0.2s ease;
        }

        .bookmark-item:hover .remove-bookmark-button {
          opacity: 1;
        }

        .remove-bookmark-button:hover {
          background-color: rgba(239, 68, 68, 0.9);
          color: white;
          transform: scale(1.1);
        }

        .remove-bookmark-button .material-icons {
          font-size: 20px;
        }

        @media (max-width: 600px) {
          .section-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .bookmarks-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }

          .remove-bookmark-button {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default BookmarksView;
